/**
 * Cross-device mock resume: a signed-in student's in-progress mock is mirrored to
 * userProfiles/{uid}/activeSittings/{level}_{topicSlug}_{formId} so it can be continued on
 * another device. localStorage (activeSitting.js) stays the source of truth on each device;
 * this module only copies sittings between it and Firestore. Guests stay local-only.
 *
 * Writes are throttled (at most one every REMOTE_WRITE_INTERVAL_MS, coalesced, sent
 * immediately when the student leaves the page). The items and grade token are written once
 * per attempt; later writes send only the small progress fields. Mirrored items carry only
 * the public display fields that /api/quiz-items serves: never an answer key (firestore.rules
 * refuses `correctAnswer` / `explanation` / `rubric` keys too).
 *
 * Every call here is best effort: a failed or slow Firestore call never breaks the exam UI.
 */
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  buildSittingBody,
  clearActiveSitting,
  findResumableSitting,
  isResumableSitting,
  sittingBankLooksCompatible,
  writeActiveSitting,
} from './activeSitting';
import { sittingTokenOwner } from './quizServer';

export const REMOTE_SITTINGS_COLLECTION = 'activeSittings';
export const REMOTE_SITTING_VERSION = 1;
/** Coalesce mirror writes to at most one per this interval while the student works. */
export const REMOTE_WRITE_INTERVAL_MS = 20 * 1000;
/** How long the exam waits for the remote copy before carrying on with what it has. */
export const REMOTE_LOOKUP_TIMEOUT_MS = 3000;
/** Limits mirrored in firestore.rules (Firestore documents cap at 1 MiB). */
export const MAX_REMOTE_ITEMS = 120;
export const MAX_QUESTIONS_JSON = 700000;
export const MAX_PROGRESS_JSON = 250000;
export const MAX_TOKEN_LENGTH = 16000;

/**
 * Display fields an item may carry in the mirror: PUBLIC_ITEM_FIELDS in src/server/quizCore.js
 * (what /api/quiz-items serves; remoteSitting.test.js keeps the two lists equal). Not imported:
 * browser code must not pull in the server bank module.
 */
export const MIRRORED_ITEM_FIELDS = [
  'id', 'ref', 'question', 'options', 'topic', 'difficultyLevel', 'cfaLevel', 'source',
  'lessonPath', 'sectionSlug', 'subtopicTags', 'vignette', 'itemSetId', 'itemSetTitle',
  'itemSetOrder', 'itemSetSize', 'setType', 'format', 'autoGrade', 'points', 'setPoints',
  'reading', 'los', 'questionVersion',
];

/** JSON keys that would mean an answer key reached the mirror (also refused by the rules). */
const ANSWER_KEY_PATTERN = /"(correctAnswer|explanation|rubric)"\s*:/;
const SYNCED_MARKER_PREFIX = 'cfa_remote_sit_synced_';

/** Document id for a mock route, '' for anything that is not mirrored (drills, review, hubs). */
export function remoteSittingId({ level = 'l1', topicSlug, formId } = {}) {
  const lv = String(level || 'l1');
  const slug = String(topicSlug || '');
  const form = Number(formId);
  if (!['l1', 'l2', 'l3'].includes(lv)) return '';
  if (!/^session-\d{1,2}$/.test(slug) || !Number.isInteger(form) || form < 1 || form > 99) return '';
  return `${lv}_${slug}_${form}`;
}

function keyPartsFromId(uid, id) {
  const m = /^(l[123])_(session-\d{1,2})_(\d{1,2})$/.exec(String(id || ''));
  return m ? { level: m[1], uid, topicSlug: m[2], formId: Number(m[3]) } : null;
}

/** Copy of an item with only the public display fields (drops keys added after grading). */
export function mirroredItem(q) {
  const out = {};
  if (!q || typeof q !== 'object') return out;
  for (const field of MIRRORED_ITEM_FIELDS) {
    if (q[field] !== undefined) out[field] = q[field];
  }
  return out;
}

const int = (v) => {
  const n = Math.round(Number(v));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Firestore fields for a sitting: `fixed` (written once per attempt: items, token, identity)
 * and `progress` (answers, position, clock). null when the sitting cannot be mirrored.
 */
export function toRemoteFields(body, keyParts) {
  if (!body || !Array.isArray(body.questions) || !body.questions.length) return null;
  if (body.questions.length > MAX_REMOTE_ITEMS) return null;
  const id = remoteSittingId(keyParts);
  const token = typeof body.sittingToken === 'string' ? body.sittingToken : '';
  if (!id || !token || token.length > MAX_TOKEN_LENGTH || !body.attemptId) return null;
  const questionsJson = JSON.stringify(body.questions.map(mirroredItem));
  const progressJson = JSON.stringify({
    selectedAnswers: Array.isArray(body.selectedAnswers) ? body.selectedAnswers : [],
    flagged: Array.isArray(body.flagged) ? body.flagged : [],
    struck: body.struck && typeof body.struck === 'object' ? body.struck : {},
    highlights: body.highlights && typeof body.highlights === 'object' ? body.highlights : {},
    questionSeconds: Array.isArray(body.questionSeconds) ? body.questionSeconds : [],
  });
  if (questionsJson.length > MAX_QUESTIONS_JSON || progressJson.length > MAX_PROGRESS_JSON) return null;
  if (ANSWER_KEY_PATTERN.test(questionsJson) || ANSWER_KEY_PATTERN.test(progressJson)) return null;
  const total = body.questions.length;
  const answered = (body.selectedAnswers || []).filter((a) => a !== null && a !== undefined && a !== '').length;
  return {
    id,
    fixed: {
      v: REMOTE_SITTING_VERSION,
      level: keyParts.level || 'l1',
      topicSlug: keyParts.topicSlug,
      formId: Number(keyParts.formId),
      attemptId: String(body.attemptId).slice(0, 80),
      sittingToken: token,
      issuedAtMs: int(body.issuedAtMs),
      durationSeconds: Math.min(86400, int(body.durationSeconds)),
      total,
      questionsJson,
    },
    progress: {
      // Sent with every progress write: the rules refuse progress for an attempt other than
      // the one whose items the document holds (another device may have replaced it).
      attemptId: String(body.attemptId).slice(0, 80),
      currentQuestion: Math.min(total - 1, int(body.currentQuestion)),
      answered: Math.min(total, answered),
      quizStartTimeMs: int(body.quizStartTimeMs),
      endsAtMs: int(body.endsAtMs),
      leftAtMs: int(body.leftAtMs),
      updatedAtMs: int(body.updatedAtMs),
      progressJson,
    },
  };
}

/** Sitting record (activeSitting.js shape) from a mirror document, null when unreadable. */
export function fromRemoteFields(data) {
  if (!data || data.v !== REMOTE_SITTING_VERSION) return null;
  let questions;
  let progress;
  try {
    questions = JSON.parse(data.questionsJson);
    progress = JSON.parse(data.progressJson);
  } catch {
    return null;
  }
  if (!Array.isArray(questions) || !questions.length || !progress || typeof progress !== 'object') return null;
  return {
    attemptId: data.attemptId,
    level: data.level,
    questions: questions.map(mirroredItem),
    currentQuestion: Number(data.currentQuestion) || 0,
    selectedAnswers: Array.isArray(progress.selectedAnswers) ? progress.selectedAnswers : [],
    flagged: Array.isArray(progress.flagged) ? progress.flagged : [],
    struck: progress.struck || {},
    highlights: progress.highlights || {},
    questionSeconds: Array.isArray(progress.questionSeconds) ? progress.questionSeconds : [],
    quizStartTimeMs: Number(data.quizStartTimeMs) || 0,
    issuedAtMs: Number(data.issuedAtMs) || 0,
    endsAtMs: Number(data.endsAtMs) || 0,
    durationSeconds: Number(data.durationSeconds) || 0,
    sittingToken: typeof data.sittingToken === 'string' ? data.sittingToken : '',
    leftAtMs: Number(data.leftAtMs) || 0,
    updatedAtMs: Number(data.updatedAtMs) || 0,
  };
}

/** Firestore access, injectable for tests. get → data | null (missing); list → [{ id, data }]. */
export const firestoreSittingStore = {
  async get(uid, id) {
    const snap = await getDoc(doc(db, 'userProfiles', uid, REMOTE_SITTINGS_COLLECTION, id));
    return snap.exists() ? snap.data() : null;
  },
  async list(uid) {
    const snap = await getDocs(collection(db, 'userProfiles', uid, REMOTE_SITTINGS_COLLECTION));
    return snap.docs.map((d) => ({ id: d.id, data: d.data() }));
  },
  set(uid, id, fields) {
    return setDoc(doc(db, 'userProfiles', uid, REMOTE_SITTINGS_COLLECTION, id), {
      ...fields,
      updatedAt: serverTimestamp(),
    });
  },
  update(uid, id, fields) {
    return updateDoc(doc(db, 'userProfiles', uid, REMOTE_SITTINGS_COLLECTION, id), {
      ...fields,
      updatedAt: serverTimestamp(),
    });
  },
  remove(uid, id) {
    return deleteDoc(doc(db, 'userProfiles', uid, REMOTE_SITTINGS_COLLECTION, id));
  },
  /** Delete only while the document still holds this attempt (another device may have replaced it). */
  async removeAttempt(uid, id, attemptId) {
    const ref = doc(db, 'userProfiles', uid, REMOTE_SITTINGS_COLLECTION, id);
    // Never an unconditional delete: it could remove another device's newer attempt. Finish has
    // just reached the server to be graded, so the transaction normally has the network too.
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (snap.exists() && snap.data()?.attemptId === attemptId) tx.delete(ref);
    });
  },
};

/* Marker: this browser's sitting (attemptId) reached the mirror. If the mirror later has no
   document for it, the sitting was finished or discarded on another device. */
function markerKey(uid, id) {
  return `${SYNCED_MARKER_PREFIX}${uid}_${id}`;
}

function readMarker(uid, id) {
  try {
    return window.localStorage.getItem(markerKey(uid, id)) || '';
  } catch {
    return '';
  }
}

function writeMarker(uid, id, attemptId) {
  try {
    window.localStorage.setItem(markerKey(uid, id), String(attemptId));
  } catch {
    /* private mode */
  }
}

function clearMarker(uid, id) {
  try {
    window.localStorage.removeItem(markerKey(uid, id));
  } catch {
    /* ignore */
  }
}

function safe(fn, fallback) {
  try {
    const out = fn();
    if (out && typeof out.then === 'function') return out.catch(() => fallback);
    return out;
  } catch {
    return fallback;
  }
}

function withTimeout(promise, ms) {
  let timer;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error('remote sitting lookup timed out')), ms);
    }),
  ]).finally(() => clearTimeout(timer));
}

/** True when a mirrored sitting can be resumed by this account on this level. */
export function remoteSittingUsable(remote, { uid, level, now = Date.now() } = {}) {
  if (!remote || !remote.sittingToken || !uid) return false;
  // grade-sitting only marks a signed-in sitting for the account it was issued to.
  if (sittingTokenOwner(remote.sittingToken) !== uid) return false;
  return isResumableSitting(remote, { level, now }) && sittingBankLooksCompatible(remote, level);
}

/**
 * Bring one route's mirror and local copy together: the newer sitting (updatedAtMs) wins and
 * is stored locally, so the usual findResumableSitting() resumes it. A local sitting that this
 * browser mirrored, now missing from the mirror, was finished or discarded elsewhere: dropped.
 * `data` is the mirror document (null when there is none).
 * @returns {'hydrated'|'local'|'none'|'finished-elsewhere'}
 */
/**
 * Mirror document → resumable sitting for this account, or null. Not marked away on the other
 * device (closed without a final write): its last write is the latest moment anyone worked on
 * it, so the time since then counts as a break.
 */
function usableRemote(data, { uid, level, now }) {
  const parsed = fromRemoteFields(data);
  const remote = parsed && { ...parsed, leftAtMs: parsed.leftAtMs || parsed.updatedAtMs };
  return remoteSittingUsable(remote, { uid, level, now }) ? remote : null;
}

function usableLocal(uid, keyParts, now) {
  const level = keyParts.level || 'l1';
  const found = findResumableSitting({ ...keyParts, uid, level }, { level, now });
  // A local sitting this account cannot finish (no grade token, or another account's) never
  // outranks the account's own mirrored copy.
  return found?.sittingToken && [uid, ''].includes(sittingTokenOwner(found.sittingToken)) ? found : null;
}

export function applyRemoteSitting({ uid, keyParts, data, now = Date.now() }) {
  const id = remoteSittingId(keyParts);
  const level = keyParts.level || 'l1';
  const parts = { ...keyParts, uid, level };
  const local = usableLocal(uid, keyParts, now);
  if (!data) {
    if (local && readMarker(uid, id) && readMarker(uid, id) === String(local.attemptId || '')) {
      clearActiveSitting(parts);
      clearMarker(uid, id);
      return 'finished-elsewhere';
    }
    return local ? 'local' : 'none';
  }
  const remote = usableRemote(data, { uid, level, now });
  // Expired or unreadable copies are ignored, not deleted: another device's clock may disagree,
  // and a missing document reads as "finished elsewhere" there. Finish/discard delete them.
  if (!remote) return local ? 'local' : 'none';
  if (local && (Number(local.updatedAtMs) || 0) >= remote.updatedAtMs) return 'local';
  writeActiveSitting(parts, remote, { keepUpdatedAt: true });
  writeMarker(uid, id, remote.attemptId);
  return 'hydrated';
}

/**
 * Quiz resume path: look up the mirror for one mock route and reconcile it with the local copy.
 * `done` resolves within timeoutMs to { status, remote } (status 'skip' for guests and
 * non-mock routes, 'unknown' when the lookup failed or timed out). `settled` resolves when the
 * lookup itself finishes, to false when a late answer shows a newer copy this device never
 * loaded; mirror writes wait for it (sync.hold) so a stale device never overwrites a newer
 * copy. Neither promise rejects.
 */
export function reconcileRemoteSitting({
  uid,
  keyParts,
  store = firestoreSittingStore,
  timeoutMs = REMOTE_LOOKUP_TIMEOUT_MS,
  now = () => Date.now(),
} = {}) {
  const id = keyParts ? remoteSittingId(keyParts) : '';
  if (!uid || !id) {
    return { id, done: Promise.resolve({ status: 'skip', remote: null }), settled: Promise.resolve(true) };
  }
  let lookup;
  try {
    lookup = Promise.resolve(store.get(uid, id));
  } catch (err) {
    lookup = Promise.reject(err);
  }
  let localAtStart = null;
  try {
    localAtStart = usableLocal(uid, keyParts, now());
  } catch {
    /* storage unavailable: treated as no local copy */
  }
  let late = false;
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      late = true;
      reject(new Error('remote sitting lookup timed out'));
    }, timeoutMs);
  });
  // Writes may go out once this resolves true. A copy that arrives after the exam carried on
  // without it, and is newer than what this device had, is never overwritten from here.
  const settled = lookup.then(
    (data) => {
      if (!late) return true;
      if (!data) {
        // Gone, and this device had mirrored the sitting it carried on with: it was finished or
        // discarded elsewhere, so writing it back would resurrect it.
        const marker = readMarker(uid, id);
        return !(localAtStart && marker && marker === String(localAtStart.attemptId || ''));
      }
      const remote = usableRemote(data, { uid, level: keyParts.level || 'l1', now: now() });
      return !remote || remote.updatedAtMs <= (Number(localAtStart?.updatedAtMs) || 0);
    },
    () => true
  );
  const done = Promise.race([lookup, timeout]).finally(() => clearTimeout(timer)).then(
    (data) => {
      try {
        const status = applyRemoteSitting({ uid, keyParts, data, now: now() });
        return { status, remote: data ? fromRemoteFields(data) : null };
      } catch {
        return { status: 'unknown', remote: null };
      }
    },
    () => ({ status: 'unknown', remote: null })
  );
  return { id, done, settled };
}

/**
 * Continue-card path: reconcile every mirrored mock of this account with this browser.
 * Resolves true when local storage changed (the card should re-read it). Never rejects.
 */
export async function syncRemoteSittings({
  uid,
  localMocks = [],
  store = firestoreSittingStore,
  timeoutMs = REMOTE_LOOKUP_TIMEOUT_MS,
  now = () => Date.now(),
} = {}) {
  if (!uid) return false;
  let docs;
  try {
    docs = await withTimeout(Promise.resolve(store.list(uid)), timeoutMs);
  } catch {
    return false;
  }
  if (!Array.isArray(docs)) return false;
  let changed = false;
  const seen = new Set();
  for (const entry of docs) {
    const parts = keyPartsFromId(uid, entry?.id);
    if (!parts) continue;
    seen.add(entry.id);
    const status = safe(() => applyRemoteSitting({ uid, keyParts: parts, data: entry.data, now: now() }), 'local');
    if (status === 'hydrated' || status === 'finished-elsewhere') changed = true;
  }
  for (const mock of localMocks) {
    const parts = { level: 'l1', uid, topicSlug: `session-${mock.formId}`, formId: Number(mock.formId) };
    const id = remoteSittingId(parts);
    if (!id || seen.has(id)) continue;
    const status = safe(() => applyRemoteSitting({ uid, keyParts: parts, data: null, now: now() }), 'local');
    if (status === 'finished-elsewhere') changed = true;
  }
  return changed;
}

/**
 * Throttled mirror writer, one per Quiz. schedule() takes the same payload as
 * writeActiveSitting(); writes go out at most once per intervalMs (latest state wins), or at
 * once with { immediate: true } (page hidden / closed / left). clear() deletes the mirror when
 * the sitting is finished or discarded. Nothing here throws or rejects.
 */
export function createRemoteSittingSync({
  store = firestoreSittingStore,
  intervalMs = REMOTE_WRITE_INTERVAL_MS,
  now = () => Date.now(),
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (t) => clearTimeout(t),
} = {}) {
  let lastWrite = Promise.resolve();
  let pending = null;
  let timer = null;
  let lastSentAt = 0;
  const holds = new Map();
  /** id → { attemptId, sittingToken } whose fixed fields the mirror already has. */
  const mirrored = new Map();
  const lastProgress = new Map();
  /** `${id}:${attemptId}` no longer mirrored: finished, discarded, or refused by the server. */
  const closed = new Set();
  /** Routes whose mirror holds a newer copy this device never loaded: never overwritten. */
  const blocked = new Set();

  const cancelTimer = () => {
    if (timer != null) clearTimer(timer);
    timer = null;
  };

  function send(job) {
    const { uid, id } = job;
    // Serialized here, not in schedule(): answers change far more often than writes go out.
    const fields = toRemoteFields(job.body, job.keyParts);
    if (!fields) return;
    const known = mirrored.get(id);
    const full = !known || known.attemptId !== fields.fixed.attemptId || known.sittingToken !== fields.fixed.sittingToken;
    const progressKey = `${fields.fixed.attemptId}|${JSON.stringify({ ...fields.progress, updatedAtMs: 0 })}`;
    if (!full && lastProgress.get(id) === progressKey) return;
    lastSentAt = now();
    lastProgress.set(id, progressKey);
    const owner = { attemptId: fields.fixed.attemptId, sittingToken: fields.fixed.sittingToken };
    mirrored.set(id, owner);
    const failed = () => {
      // Writes wait offline instead of failing, so a refusal is final: the document was deleted
      // (finished or discarded on another device), replaced by another attempt, or the rules
      // refuse it. Retrying with a full write would bring a finished sitting back; stop instead.
      closed.add(`${id}:${owner.attemptId}`);
      if (mirrored.get(id) === owner) mirrored.delete(id);
      lastProgress.delete(id);
      if (pending?.id === id && pending.body.attemptId === owner.attemptId) pending = null;
    };
    let write;
    try {
      write = Promise.resolve(
        full ? store.set(uid, id, { ...fields.fixed, ...fields.progress }) : store.update(uid, id, fields.progress)
      );
    } catch (err) {
      write = Promise.reject(err);
    }
    lastWrite = write;
    write.then(() => {
      if (!closed.has(`${id}:${owner.attemptId}`)) writeMarker(uid, id, owner.attemptId);
    }, failed);
  }

  function flush() {
    cancelTimer();
    if (!pending) return;
    if (holds.has(pending.id)) return; // re-flushed when the hold settles
    const job = pending;
    pending = null;
    try {
      send(job);
    } catch {
      /* never break the exam */
    }
  }

  function arm() {
    if (timer != null || !pending) return;
    const wait = Math.max(0, lastSentAt + intervalMs - now());
    timer = setTimer(() => {
      timer = null;
      flush();
    }, wait);
  }

  return {
    /** Hold writes for this route until `promise` settles (the remote lookup). */
    hold(id, promise) {
      if (!id) return;
      holds.set(id, promise);
      Promise.resolve(promise)
        .then((allow) => allow !== false, () => true)
        .then((allow) => {
          if (holds.get(id) !== promise) return;
          holds.delete(id);
          if (!allow) {
            blocked.add(id);
            if (pending?.id === id) pending = null;
            return;
          }
          if (pending?.id === id) arm();
        });
    },

    /** The mirror already holds this attempt's items (resumed from it): send progress only. */
    noteMirrored(id, remote) {
      if (id && remote?.attemptId && remote?.sittingToken) {
        mirrored.set(id, { attemptId: remote.attemptId, sittingToken: remote.sittingToken });
      }
    },

    schedule({ uid, keyParts, sitting }, { immediate = false } = {}) {
      try {
        if (!uid || !keyParts || !sitting) return false;
        const id = remoteSittingId(keyParts);
        if (!id || blocked.has(id)) return false;
        const body = buildSittingBody(sitting);
        if (sittingTokenOwner(body.sittingToken) !== uid) return false;
        if (closed.has(`${id}:${body.attemptId}`)) return false;
        if (!Array.isArray(body.questions) || !body.questions.length) return false;
        if (pending && pending.id !== id) flush();
        pending = { uid, id, keyParts: { ...keyParts }, body };
        if (immediate) flush();
        else arm();
        return true;
      } catch {
        return false;
      }
    },

    flush,
    async flushPending() {
      await Promise.all([...holds.values()]);
      flush();
      await lastWrite;
    },

    clear({ uid, keyParts, attemptId = '' } = {}) {
      try {
        const id = keyParts ? remoteSittingId(keyParts) : '';
        if (!uid || !id) return;
        if (pending?.id === id) {
          pending = null;
          cancelTimer();
        }
        if (attemptId) closed.add(`${id}:${attemptId}`);
        mirrored.delete(id);
        lastProgress.delete(id);
        clearMarker(uid, id);
        safe(() => (attemptId && store.removeAttempt ? store.removeAttempt(uid, id, attemptId) : store.remove(uid, id)));
      } catch {
        /* never break the exam */
      }
    },

    /** Pending write, for tests. */
    pending() {
      return pending;
    },
  };
}
